import { createStore } from '@oneappexchange/lwc-wired';
import { produce } from 'immer';
import { BadgeProps } from 'wds/badge';
import { State } from 'zustand';
import equalityFn from 'zustand/shallow';
import { IS_DEVELOPMENT } from '~/modules/config';
import { AuthTimeoutError } from '~/modules/errors/authErrors';
import {
    CreateReviewError,
    DeleteReviewError,
    EditReviewError,
    FetchReviewsError,
} from '~/modules/errors/reviewErrors';
import { SimpleBadgeProps } from '~/modules/helpers/badgeHelper';
import { Routes } from '~/modules/routes';
import { handleException } from '~/modules/utilities/error_utilities';
import { getLoginUrl } from '~/modules/utilities/router';
import { StoreActionResponse } from '~/modules/utilities/storesUtilities';
import {
    CommentStatus,
    DeleteReviewStatus,
    LikeStatus,
    LoadingStatus,
    REPLY_TYPES,
    ReviewService,
    ReviewSummary,
    SortBy,
    UserQuestionResponse,
    WriteReviewStatus,
} from '../services/reviewService';
import { i18nStore } from './i18nStore';
import { listingStore } from './listingStore';
import { routerStore, selectMatch } from './routerStore';
import { selectStatus, selectUserId, userStore } from './userStore';

export const DEFAULT_PAGE_LENGTH = 10;
export const DEFAULT_PAGE_NUMBER = 1;

const reviewService = new ReviewService();

export interface Reviewer {
    tbid: string;
    name: string;
    firstName: string;
    lastName: string;
    badges: Array<SimpleBadgeProps | BadgeProps>;
    avatar: string;
    profileLink: string;
}

export interface Comment {
    id: string;
    content: string;
    date: string;
    reviewId: string;
    user: Reviewer;
}

export interface Review {
    id: string;
    user: Reviewer;
    rating: number;
    date: string;
    title: string;
    content: string;
    userLikeId: string;
    likeCount: number;
    likeStatus: LikeStatus;
    comments: Comment[];
    commentStatus: CommentStatus;
    commentBoxShown: boolean;
}

export interface Filter {
    rating: string[];
    badge: string[];
}

export interface FilterChip {
    section: string;
    value: string;
    label: string;
}

export interface ListingReviews {
    reviews: Review[];
    hasNext: boolean;
    total: number;
}

export interface UserReview {
    questionResponses: UserQuestionResponse[];
    questionnaireResponseId: string;
    questionnaireId: string;
    listingId: string;
    userId: string;
}

export interface ReviewState extends State {
    loadingStatus: LoadingStatus;
    currentPage: number;
    listingReviews: ListingReviews;
    filter: Filter;
    sort: string;
    listingId: string;
    writeReviewStatus: WriteReviewStatus;
    hasListingReviewsError: boolean;
    //This is for inline errors on the CUD modals
    errorMsg: string;
    //This is for the inline error on the reviews list
    loadMoreError: string;

    // User review state
    userReview?: UserReview;
    isUserReviewLoading: boolean;

    // Single review state (when revId parameter is in URL)
    singleReview?: Review;
    singleReviewId: string;
    isLoadingSingleReview: boolean;
    isReportingAbuse: boolean;
    deleteReviewStatus: DeleteReviewStatus;
}

const initialState: ReviewState = {
    loadingStatus: LoadingStatus.UNINITILIAZED,
    currentPage: DEFAULT_PAGE_NUMBER,
    listingReviews: {
        reviews: [],
        hasNext: false,
        total: 0,
    },
    filter: {
        rating: [],
        badge: [],
    },
    sort: SortBy.RECENT,
    listingId: '',
    singleReviewId: '',
    isUserReviewLoading: false,
    writeReviewStatus: WriteReviewStatus.AVAILABLE,
    isLoadingSingleReview: false,
    isReportingAbuse: false,
    deleteReviewStatus: DeleteReviewStatus.AVAILABLE,
    hasListingReviewsError: false,
    errorMsg: '',
    loadMoreError: '',
};

type NotificationActionKey = keyof I18nMessages['notifications']['reviewStore'];

function getNotificationLabels(key: NotificationActionKey) {
    const authMessages = i18nStore.getState().i18n.notifications.authentication;
    const notificationLabels = key ? i18nStore.getState().i18n.notifications.reviewStore[key] : undefined;
    return { authMessages, notificationLabels };
}

export const reviewStore = createStore(
    initialState,

    (set: SetState<ReviewState>, get: GetState<ReviewState>) => ({
        async fetchReviewsForListing(
            listingId: string,
            pageLength: number = DEFAULT_PAGE_LENGTH,
            loading: LoadingStatus = LoadingStatus.LOADING,
        ) {
            try {
                const builtFilters = buildFilters(get().filter);

                set(function setLoading(draft) {
                    draft.loadingStatus = loading;
                    draft.hasListingReviewsError = false;
                });

                const listingReviews: ListingReviews = await reviewService.getReviews(
                    listingId,
                    pageLength,
                    get().currentPage,
                    builtFilters,
                    get().sort,
                );

                set(function loadReviews(draft: ReviewState) {
                    if (get().currentPage !== 1) {
                        draft.listingReviews.reviews = draft.listingReviews.reviews.concat(listingReviews.reviews);
                        draft.listingReviews.hasNext = listingReviews.hasNext;
                    } else {
                        draft.listingReviews = listingReviews || {
                            reviews: [],
                            hasNext: false,
                            total: 0,
                        };
                    }
                    draft.listingId = listingId;
                    draft.loadingStatus = LoadingStatus.LOADED;
                });

                return Promise.resolve();
            } catch (error) {
                set(function setLoading(draft) {
                    draft.loadingStatus = LoadingStatus.ERROR;
                    draft.hasListingReviewsError = true;
                });
                handleException(error, 'reviewStore.fetchReviewsForListing');
                return Promise.reject(new FetchReviewsError('Error while fetching reviews'));
            }
        },

        async fetchUserReviewForListing(listingId: string, userId: string) {
            try {
                set(function setIsUserReviewLoading(draft) {
                    draft.isUserReviewLoading = true;
                });

                const userReview: UserReview = await reviewService.getReviewForUser(listingId, userId);

                set(function loadUserReview(draft: ReviewState) {
                    draft.userReview = userReview;
                    draft.isUserReviewLoading = false;
                });
            } catch (error) {
                handleException(error, 'reviewStore.fetchUserReviewForListing');
                set(function setError(draft: ReviewState) {
                    draft.isUserReviewLoading = false;
                    draft.userReview = undefined;
                });
            }
        },

        async createReview(
            listingId: string,
            reviewDto: { title: string; rating: string; description: string },
        ): Promise<StoreActionResponse> {
            const loginUrl = getLoginUrl();
            const response: StoreActionResponse = { isSuccess: false };
            const { authMessages, notificationLabels } = getNotificationLabels('createReview');

            set(function beginCreatingReview(draft) {
                draft.writeReviewStatus = WriteReviewStatus.IN_PROGRESS;
                draft.errorMsg = '';
            });

            try {
                if (!get().userReview) {
                    throw new CreateReviewError('User review was not loaded');
                }
                // @ts-expect-error * Automated Type Error Fix
                const modifiedReview = produceReview(get().userReview, reviewDto);
                const review = await reviewService.createReview(modifiedReview);

                await reviewStore.fetchReviewsForListing(listingId);
                updateReviewSummary(listingId);

                set(function createdReview(draft: ReviewState) {
                    draft.userReview = review;
                    draft.writeReviewStatus = WriteReviewStatus.AVAILABLE;
                });

                response.isSuccess = true;
                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
            } catch (error) {
                // @ts-expect-error * Automated Type Error Fix
                response.errorMessage = notificationLabels.errorMessage();
                response.exceptionInfo = {
                    context: 'reviewStore.createReview',
                    error,
                };

                if (error instanceof AuthTimeoutError) {
                    const errorMsg = authMessages.errorMessage({ LOGIN_URL: loginUrl });
                    response.errorMessage = errorMsg;
                    set(function timedOutCreatingReview(draft) {
                        draft.writeReviewStatus = WriteReviewStatus.AVAILABLE;
                        draft.errorMsg = errorMsg;
                    });
                }

                if (error instanceof CreateReviewError) {
                    set(function failedCreatingReview(draft) {
                        draft.writeReviewStatus = WriteReviewStatus.AVAILABLE;
                        // @ts-expect-error * Automated Type Error Fix
                        draft.errorMsg = notificationLabels.errorMessage();
                    });
                }

                if (error instanceof FetchReviewsError) {
                    reviewStore.setErrorState();
                }
            }

            return response;
        },

        async editReview(
            listingId: string,
            reviewDto: { title: string; rating: string; description: string },
        ): Promise<StoreActionResponse> {
            const loginUrl = getLoginUrl();
            const response: StoreActionResponse = { isSuccess: false };
            const { authMessages, notificationLabels } = getNotificationLabels('updateReview');

            set(function beginEditingReview(draft) {
                draft.writeReviewStatus = WriteReviewStatus.IN_PROGRESS;
                draft.errorMsg = '';
            });

            try {
                // @ts-expect-error * Automated Type Error Fix
                const modifiedReview = produceReview(get().userReview, reviewDto);
                const updatedReview = await reviewService.editReview(modifiedReview);

                await reviewStore.fetchReviewsForListing(listingId);
                updateReviewSummary(listingId);

                set(function editedReview(draft: ReviewState) {
                    draft.userReview = updatedReview;
                    draft.writeReviewStatus = WriteReviewStatus.AVAILABLE;
                });

                response.isSuccess = true;
                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
            } catch (error) {
                // @ts-expect-error * Automated Type Error Fix
                response.errorMessage = notificationLabels.errorMessage();
                response.exceptionInfo = {
                    context: 'reviewStore.editReview',
                    error,
                };

                if (error instanceof AuthTimeoutError) {
                    const errorMsg = authMessages.errorMessage({ LOGIN_URL: loginUrl });
                    response.errorMessage = errorMsg;
                    set(function timedOutEditingReview(draft) {
                        draft.writeReviewStatus = WriteReviewStatus.AVAILABLE;
                        draft.errorMsg = errorMsg;
                    });
                }

                if (error instanceof EditReviewError) {
                    set(function failedEditingReview(draft) {
                        draft.writeReviewStatus = WriteReviewStatus.AVAILABLE;
                        // @ts-expect-error * Automated Type Error Fix
                        draft.errorMsg = notificationLabels.errorMessage();
                    });
                }

                if (error instanceof FetchReviewsError) {
                    reviewStore.setErrorState();
                }
            }

            return response;
        },

        async deleteReview(listingId: string): Promise<StoreActionResponse> {
            const loginUrl = getLoginUrl();
            const response: StoreActionResponse = { isSuccess: false };
            const { authMessages, notificationLabels } = getNotificationLabels('deleteReview');

            set(function beginDeletingReview(draft) {
                draft.deleteReviewStatus = DeleteReviewStatus.IN_PROGRESS;
                draft.errorMsg = '';
            });

            try {
                // @ts-expect-error * Automated Type Error Fix
                await reviewService.deleteReview(get().userReview.questionnaireResponseId);
                await Promise.all([
                    reviewStore.fetchReviewsForListing(listingId),
                    // @ts-expect-error * Automated Type Error Fix
                    reviewStore.fetchUserReviewForListing(listingId, userStore.getState().userId),
                    updateReviewSummary(listingId),
                ]);

                set(function deletedReview(draft) {
                    draft.deleteReviewStatus = DeleteReviewStatus.AVAILABLE;
                });
                response.isSuccess = true;
                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
            } catch (error) {
                // @ts-expect-error * Automated Type Error Fix
                response.errorMessage = notificationLabels.errorMessage();
                response.exceptionInfo = {
                    context: 'reviewStore.deleteReview',
                    error,
                };

                if (error instanceof AuthTimeoutError) {
                    const errorMsg = authMessages.errorMessage({ LOGIN_URL: loginUrl });
                    response.errorMessage = errorMsg;
                    set(function timedOutDeletingReview(draft) {
                        draft.deleteReviewStatus = DeleteReviewStatus.AVAILABLE;
                        draft.errorMsg = errorMsg;
                    });
                }

                if (error instanceof DeleteReviewError) {
                    set(function failedDeletingReview(draft) {
                        draft.deleteReviewStatus = DeleteReviewStatus.AVAILABLE;
                        // @ts-expect-error * Automated Type Error Fix
                        draft.errorMsg = notificationLabels.errorMessage();
                    });
                }
            }

            return response;
        },

        async loadMore(listingId: string, pageLength: number = DEFAULT_PAGE_LENGTH) {
            const { notificationLabels } = getNotificationLabels('loadMoreReviews');
            set(function loadingMoreReviews(draft) {
                draft.currentPage++;
                draft.loadMoreError = '';
            });
            try {
                await reviewStore.fetchReviewsForListing(listingId, pageLength, LoadingStatus.LOADING_MORE);
            } catch (error) {
                handleException(error, 'reviewStore.loadMore');
                set(function failedLoadingMore(draft) {
                    draft.loadingStatus = LoadingStatus.LOADED;
                    // @ts-expect-error * Automated Type Error Fix
                    draft.loadMoreError = notificationLabels.errorMessage();
                });
            }
        },

        async likeReview(reviewId: string): Promise<StoreActionResponse> {
            const loginUrl = getLoginUrl();
            const response: StoreActionResponse = { isSuccess: false };
            const { authMessages, notificationLabels } = getNotificationLabels('likeReview');

            set(function beginLikingReview(draft) {
                if (draft.singleReviewId === reviewId) {
                    // @ts-expect-error * Automated Type Error Fix
                    draft.singleReview.likeStatus = LikeStatus.inProgress;
                    // @ts-expect-error * Automated Type Error Fix
                    draft.singleReview.likeCount++;
                }

                for (const review of draft.listingReviews.reviews) {
                    if (review.id === reviewId) {
                        review.likeStatus = LikeStatus.inProgress;
                        review.likeCount++;
                    }
                }
            });

            try {
                // @ts-expect-error * Automated Type Error Fix
                const updatedReview = await reviewService.createReply(reviewId, null, REPLY_TYPES.Helpful);

                set(function likedReview(draft) {
                    if (draft.singleReviewId === updatedReview.id) {
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.userLikeId = updatedReview.userLikeId;
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.likeStatus = updatedReview.likeStatus;
                    }
                    for (const review of draft.listingReviews.reviews) {
                        if (review.id === updatedReview.id) {
                            review.userLikeId = updatedReview.userLikeId;
                            review.likeStatus = updatedReview.likeStatus;
                        }
                    }
                });

                response.isSuccess = true;
                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
            } catch (error) {
                // Undo changes made to the review since we ran into an error
                set(function failedLikingReview(draft) {
                    if (draft.singleReviewId === reviewId) {
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.likeStatus = LikeStatus.unliked;
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.likeCount--;
                    }

                    for (const review of draft.listingReviews.reviews) {
                        if (review.id === reviewId) {
                            review.likeStatus = LikeStatus.unliked;
                            review.likeCount--;
                        }
                    }
                });

                response.exceptionInfo = {
                    context: 'reviewStore.likeReview',
                    error,
                };
                response.errorMessage =
                    error instanceof AuthTimeoutError
                        ? authMessages.errorMessage({ LOGIN_URL: loginUrl })
                        : // @ts-expect-error * Automated Type Error Fix
                          notificationLabels.errorMessage();
            }

            return response;
        },

        async unlikeReview(reviewId: string, replyId: string): Promise<StoreActionResponse> {
            const loginUrl = getLoginUrl();
            const response: StoreActionResponse = { isSuccess: false };
            const { authMessages, notificationLabels } = getNotificationLabels('unlikeReview');

            set(function beginUnlikingReview(draft) {
                if (draft.singleReview) {
                    if (draft.singleReviewId === reviewId) {
                        draft.singleReview.likeCount--;
                        draft.singleReview.likeStatus = LikeStatus.inProgress;
                    }
                }
                for (const review of draft.listingReviews.reviews) {
                    if (review.id === reviewId) {
                        review.likeCount--;
                        review.likeStatus = LikeStatus.inProgress;
                    }
                }
            });

            try {
                const updatedReview = await reviewService.unlikeReview(reviewId, replyId);

                set(function unlikedReview(draft) {
                    if (draft.singleReview) {
                        if (draft.singleReviewId === updatedReview.id) {
                            draft.singleReview.userLikeId = updatedReview.userLikeId;
                            draft.singleReview.likeStatus = updatedReview.likeStatus;
                        }
                    }
                    for (const review of draft.listingReviews.reviews) {
                        if (review.id === updatedReview.id) {
                            review.userLikeId = updatedReview.userLikeId;
                            review.likeStatus = updatedReview.likeStatus;
                        }
                    }
                });

                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
                response.isSuccess = true;
            } catch (error) {
                // Undo changes made to the review since we ran into an error
                set(function failedUnlikingReview(draft) {
                    if (draft.singleReview) {
                        if (draft.singleReviewId === reviewId) {
                            draft.singleReview.likeStatus = LikeStatus.liked;
                            draft.singleReview.likeCount++;
                        }
                    }
                    for (const review of draft.listingReviews.reviews) {
                        if (review.id === reviewId) {
                            review.likeStatus = LikeStatus.liked;
                            review.likeCount++;
                        }
                    }
                });

                response.exceptionInfo = {
                    context: 'reviewStore.unlikeReview',
                    error,
                };
                response.errorMessage =
                    error instanceof AuthTimeoutError
                        ? authMessages.errorMessage({ LOGIN_URL: loginUrl })
                        : // @ts-expect-error * Automated Type Error Fix
                          notificationLabels.errorMessage();
            }

            return response;
        },

        async comment(reviewId: string, comment: string, isSingleReview: boolean) {
            const loginUrl = getLoginUrl();
            const response: StoreActionResponse = { isSuccess: false };
            const { authMessages, notificationLabels } = getNotificationLabels('commentReview');

            set(function beginCommentingReview(draft) {
                if (isSingleReview) {
                    // @ts-expect-error * Automated Type Error Fix
                    draft.singleReview.commentStatus = CommentStatus.IN_PROGRESS;
                }
                for (const review of draft.listingReviews.reviews) {
                    if (review.id === reviewId) {
                        review.commentStatus = CommentStatus.IN_PROGRESS;
                    }
                }
            });

            try {
                const updatedReview = await reviewService.createReply(reviewId, comment, REPLY_TYPES.Comment);

                set(function commentedReview(draft) {
                    if (isSingleReview) {
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.comments = updatedReview.comments;
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.commentStatus = CommentStatus.AVAILABLE;
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.commentBoxShown = false;
                    }
                    for (const review of draft.listingReviews.reviews) {
                        if (review.id === updatedReview.id) {
                            review.comments = updatedReview.comments;
                            review.commentStatus = CommentStatus.AVAILABLE;
                            review.commentBoxShown = false;
                        }
                    }
                });

                response.isSuccess = true;
                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
            } catch (error) {
                set(function failedCommentingReview(draft) {
                    if (isSingleReview) {
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.commentStatus = CommentStatus.AVAILABLE;
                        // @ts-expect-error * Automated Type Error Fix
                        draft.singleReview.commentBoxShown = false;
                    }
                    for (const review of draft.listingReviews.reviews) {
                        if (review.id === reviewId) {
                            review.commentStatus = CommentStatus.AVAILABLE;
                            review.commentBoxShown = false;
                        }
                    }
                });

                response.exceptionInfo = {
                    context: 'reviewStore.unlikeReview',
                    error,
                };
                response.errorMessage =
                    error instanceof AuthTimeoutError
                        ? authMessages.errorMessage({ LOGIN_URL: loginUrl })
                        : // @ts-expect-error * Automated Type Error Fix
                          notificationLabels.errorMessage();
            }

            return response;
        },

        openCommentBox(reviewId: string, isSingleReview: boolean) {
            set(function openCommentBox(draft) {
                if (isSingleReview) {
                    // @ts-expect-error * Automated Type Error Fix
                    draft.singleReview.commentBoxShown = true;
                    return;
                }
                for (const review of draft.listingReviews.reviews) {
                    if (review.id === reviewId) {
                        review.commentBoxShown = true;
                    }
                }
            });
        },

        closeCommentBox(reviewId: string, isSingleReview: boolean) {
            set(function closeCommentBox(draft) {
                if (isSingleReview) {
                    // @ts-expect-error * Automated Type Error Fix
                    draft.singleReview.commentBoxShown = false;
                    return;
                }
                for (const review of draft.listingReviews.reviews) {
                    if (review.id === reviewId) {
                        review.commentBoxShown = false;
                    }
                }
            });
        },

        async setFilter(listingId: string, filter: { section: string; value: string[] }) {
            set(function setFilter(draft) {
                // @ts-expect-error * Automated Type Error Fix
                draft.filter[filter.section] = filter.value;
                draft.currentPage = DEFAULT_PAGE_NUMBER;
            });

            try {
                await reviewStore.fetchReviewsForListing(listingId, undefined, LoadingStatus.FILTERING);
            } catch (error) {
                handleException(error, 'reviewStore.setFilter');
                if (error instanceof FetchReviewsError) {
                    reviewStore.setErrorState();
                }
            }
        },

        async removeFilter(listingId: string, filter: { section: string; value: string }) {
            set(function removeFilter(draft) {
                // @ts-expect-error * Automated Type Error Fix
                draft.filter[filter.section] = draft.filter[filter.section].filter(
                    (filterValue: string) => filterValue !== filter.value,
                );
                draft.currentPage = DEFAULT_PAGE_NUMBER;
            });
            try {
                await reviewStore.fetchReviewsForListing(listingId, undefined, LoadingStatus.FILTERING);
            } catch (error) {
                handleException(error, 'reviewStore.removeFilter');
                if (error instanceof FetchReviewsError) {
                    reviewStore.setErrorState();
                }
            }
        },

        async setSort(listingId: string, sort: string) {
            set(function setSort(draft) {
                draft.sort = sort;
                draft.currentPage = DEFAULT_PAGE_NUMBER;
            });

            try {
                await reviewStore.fetchReviewsForListing(listingId, undefined, LoadingStatus.SORTING);
            } catch (error) {
                handleException(error, 'reviewStore.setSort');
                if (error instanceof FetchReviewsError) {
                    reviewStore.setErrorState();
                }
            }
        },
        async reportAbuse(reviewId: string, comment: string): Promise<StoreActionResponse> {
            const loginUrl = getLoginUrl();
            const response: StoreActionResponse = { isSuccess: false };
            const { authMessages, notificationLabels } = getNotificationLabels('reportReview');

            set(function beginReportingAbuse(draft) {
                draft.isReportingAbuse = true;
            });

            try {
                await reviewService.createReply(reviewId, comment, REPLY_TYPES['Report Abuse']);
                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
                response.isSuccess = true;
            } catch (error) {
                response.exceptionInfo = {
                    context: 'reviewStore.reportAbuse',
                    error,
                };
                response.errorMessage =
                    error instanceof AuthTimeoutError
                        ? authMessages.errorMessage({ LOGIN_URL: loginUrl })
                        : // @ts-expect-error * Automated Type Error Fix
                          notificationLabels.errorMessage();
            } finally {
                set(function finishedReportingAbuse(draft) {
                    draft.isReportingAbuse = false;
                });
            }

            return response;
        },

        areFiltersEmpty(): boolean {
            return get().filter.badge.length === 0 && get().filter.rating.length === 0;
        },

        /** This functions decodes the title and description, which may contain html encoded text */
        getReviewValues(): { title: string; rating: string; description: string } {
            try {
                // @ts-expect-error * Automated Type Error Fix
                const title = get().userReview.questionResponses.filter(
                    (question) => question.questionName === 'Title',
                )[0].response;
                // @ts-expect-error * Automated Type Error Fix
                const description = get().userReview.questionResponses.filter(
                    (question) => question.questionName === 'Comments',
                )[0].response;
                // @ts-expect-error * Automated Type Error Fix
                const rating = get().userReview.questionResponses.filter(
                    (question) => question.questionName === 'Overall',
                )[0].response;

                return {
                    title,
                    description,
                    rating,
                };
            } catch (error) {
                handleException(error, 'reviewStore.getReviewValues');
                return {
                    title: '',
                    description: '',
                    rating: '',
                };
            }
        },

        reset() {
            set(function resetStore() {
                return initialState;
            });
        },

        // Single review actions
        async fetchSingleReview(reviewId: string) {
            set(function beginLoadingSingleReview(draft: ReviewState) {
                draft.singleReviewId = reviewId;
                draft.isLoadingSingleReview = true;
            });
            try {
                const review = await reviewService.getReview(reviewId);

                set(function loadedSingleReview(draft: ReviewState) {
                    draft.singleReview = review;
                    draft.isLoadingSingleReview = false;
                });
            } catch (error) {
                handleException(error, 'reviewStore.fetchSingleReview');
                set(function failedLoadingSingleReview(draft: ReviewState) {
                    draft.singleReviewId = '';
                    draft.isLoadingSingleReview = false;
                });
            }
        },
        resetSingleReview() {
            set(function resetSingleReview(draft: ReviewState) {
                draft.singleReviewId = '';
                draft.singleReview = undefined;
                draft.isLoadingSingleReview = false;
            });
        },
        resetErrorMsg() {
            set(function resetErrorMsg(draft: ReviewState) {
                draft.errorMsg = '';
            });
        },
        setErrorState() {
            set(function setErrorState(draft: ReviewState) {
                draft.loadingStatus = LoadingStatus.ERROR;
                draft.hasListingReviewsError = true;
                draft.listingReviews = {
                    reviews: [],
                    hasNext: false,
                    total: 0,
                };
            });
        },
    }),
    'reviewStore',
    IS_DEVELOPMENT,
);

function buildFilters(filter: Filter) {
    const parsedRatingsFilter = filter.rating.map((rating) => 'rt' + rating);
    return filter.badge.concat(parsedRatingsFilter).join(',');
}

function checkShouldLoadUserReview() {
    const { params, url } = selectMatch(routerStore.getState());
    const userId = userStore.getState().userId;
    const reviewState = reviewStore.getState();

    if (Routes.LISTING !== url) {
        return;
    }

    if (reviewState.userReview?.userId === userId || reviewState.isUserReviewLoading) {
        return;
    }

    // @ts-expect-error * Automated Type Error Fix
    if (Boolean(userId) && params.tab === 'r') {
        try {
            // @ts-expect-error * Automated Type Error Fix
            reviewStore.fetchUserReviewForListing(params.listingId, userId);
        } catch (error) {
            handleException(error, 'reviewStore.checkShouldLoadUserReview');
            if (error instanceof FetchReviewsError) {
                reviewStore.setErrorState();
            }
        }
    }
}

async function checkShouldLoadAllReviews() {
    const { params, url } = selectMatch(routerStore.getState());
    const reviewState = reviewStore.getState();

    // If we're not on the correct page, exit
    if (Routes.LISTING !== url) {
        reviewStore.reset();
        return;
    }

    // Don't call anything until the user is loaded.
    if (userStore.getState().isLoading) {
        return;
    }

    // Order matters here, fetchSingleReview needs to happen before fetchReviewsForListing because we access 'singleReviewId' in the latter function
    // @ts-expect-error * Automated Type Error Fix
    if (params.revId) {
        // If we have a revId, and we're not yet loading the single review, and it's different than what we already have loaded in the store, load it
        // @ts-expect-error * Automated Type Error Fix
        if (!reviewState.isLoadingSingleReview && reviewState.singleReview?.id !== params.revId) {
            // @ts-expect-error * Automated Type Error Fix
            reviewStore.fetchSingleReview(params.revId);
        }
    } else {
        reviewStore.resetSingleReview();
    }

    // If we haven't loaded reviews yet, and we're in the right tab, load them
    // @ts-expect-error * Automated Type Error Fix
    if (reviewState.listingId !== params.listingId && params.tab === 'r') {
        // @ts-expect-error * Automated Type Error Fix
        reviewStore.fetchReviewsForListing(params.listingId);
    }
}

function produceReview(userReview: UserReview, reviewDto: { title: string; rating: string; description: string }) {
    const modifiedReview = produce(userReview, (draft) => {
        for (const questionResponse of draft.questionResponses) {
            if (questionResponse.questionName === 'Overall') {
                questionResponse.response = reviewDto.rating;
            }
            if (questionResponse.questionName === 'Title') {
                questionResponse.response = reviewDto.title;
            }
            if (questionResponse.questionName === 'Comments') {
                questionResponse.response = reviewDto.description;
            }
        }
    });

    return modifiedReview;
}

async function updateReviewSummary(listingId: string) {
    try {
        const summary: ReviewSummary = await reviewService.getSummary(listingId);
        listingStore.updateReviewSummary(listingId, summary);
    } catch (error) {
        handleException(error, 'reviewStore.updateReviewSummary');
    }
}

userStore.subscribe(checkShouldLoadUserReview, selectUserId);
userStore.subscribe(checkShouldLoadAllReviews, selectStatus, equalityFn);

routerStore.subscribe(checkShouldLoadUserReview, selectMatch, equalityFn);
routerStore.subscribe(checkShouldLoadAllReviews, selectMatch, equalityFn);

checkShouldLoadAllReviews();
